import type {
  CustomerRateBand,
  CustomerRateCard,
  CustomerRateSheet,
  CustomerRateSheetLine,
  CustomerRouteSheet,
  OrderTask,
  RatingUomValue,
  TaskTypeValue,
} from '@my/tms-api';
import { CARGO_HANDLING_TASK_TYPES, cargoHandlingPhaseFor } from './cargoPick';

/**
 * Transport-charge pricing from a customer's rate card — the piece the rate
 * engine was missing. `rateEngine.ts` applies the fuel surcharge and the
 * additionals; this answers "what does THIS job cost to move", from the
 * customer's rate sheets and route sheets.
 *
 * <p>
 * Pure functions, no React. The Charges panel decides what to DO with a quote
 * (apply it, ask first, offer a re-price); this only says what the rate card
 * gives and why.
 * </p>
 *
 * <p>
 * Resolution order, and why only one answer can come out:
 *   1. A RATE SHEET for the order's cargo type, in effect on the job date.
 *   2. Otherwise a ROUTE SHEET whose lane matches the job — an Exact lane
 *      before a Contains one, since Exact is the more specific agreement.
 * The rate-card handlers already refuse a cargo type priced by both kinds over
 * an overlapping period, so (1) and (2) never compete.
 * </p>
 */

/** What the job looks like, as far as pricing cares. Built by the order page. */
export interface TransportJob {
  cargoDefinitionId: string;
  /**
   * Container / tank cargo: each cargo row is a physical unit, recorded again
   * on every leg it travels. Quantities are then read once per unit rather
   * than summed across legs — see `measure`.
   */
  isUnitCargo: boolean;
  /** Units per trailer on unit cargo (containers / tanks) — 1 or 2. */
  unitCount: number;
  /** The order's currency; a sheet in another currency is not applied. */
  currency: string;
  /** The order's tasks, in any order — cancelled ones are ignored here. */
  tasks: OrderTask[];
  /** Pallet types of the order's cargo definition, to resolve the saved name to an id. */
  palletVariants: { id: string; name: string }[];
  /**
   * Location id → name, for saying which lane the job runs when no route price
   * matches it. Optional: without it the explanation just says less.
   */
  locationNames?: Record<string, string>;
  /** Cargo definition id → display name, for the same purpose. */
  cargoNames?: Record<string, string>;
}

export type TransportQuote =
  | {
      kind: 'priced';
      net: number;
      autoApply: boolean;
      /** The sheet it came from — identifies "the same price" across recomputes. */
      sheetId: string;
      /** One line for the operator: where the price came from. */
      source: string;
      /** How it was worked out, e.g. "12 pallets × €40.00". */
      detail: string;
    }
  | {
      /**
       * The customer HAS transport pricing for this cargo type, but it can't
       * price this job — worth saying, because the operator expects a price.
       */
      kind: 'unpriced';
      reason: string;
    }
  | {
      /** Nothing on the rate card applies at all — the usual case, stays quiet. */
      kind: 'none';
    };

const round2 = (n: number) => Math.round(n * 100) / 100;

/** `yyyy-mm-dd` strings compare correctly as strings. */
function inEffect(sheet: { effectiveFrom: string; effectiveTo?: string | null }, date: string): boolean {
  const from = sheet.effectiveFrom.slice(0, 10);
  const to = sheet.effectiveTo?.slice(0, 10) ?? null;
  return from <= date && (to === null || date <= to);
}

function liveTasks(tasks: OrderTask[]): OrderTask[] {
  return tasks.filter((t) => t.state !== 'Cancelled').sort((a, b) => a.sequence - b.sequence);
}

/** Collections and deliveries only — a Stop, Wash or Inspection doesn't move cargo. */
function handlingTasks(tasks: OrderTask[]): OrderTask[] {
  return tasks.filter((t) => CARGO_HANDLING_TASK_TYPES.has(t.type));
}

const isLoad = (t: OrderTask) => cargoHandlingPhaseFor(t.type) === 'load';

/**
 * The date that decides which sheet is in effect: when the job RUNS, so a
 * price change on the 1st applies to jobs running from the 1st. The first
 * collection's planned date, else the first task's, else today.
 */
export function jobDate(tasks: OrderTask[], today: string): string {
  const live = liveTasks(tasks);
  const firstCollection = live.find((t) => CARGO_HANDLING_TASK_TYPES.has(t.type) && isLoad(t));
  return (
    firstCollection?.plannedDate?.slice(0, 10) ??
    live.find((t) => t.plannedDate)?.plannedDate?.slice(0, 10) ??
    today
  );
}

// ── Quantities ──────────────────────────────────────────────────────────────

/**
 * How much of a unit of measure the job carries, read from what was COLLECTED
 * — the load-side legs. Delivery legs only draw from those collections, so
 * counting both would bill the same pallets twice.
 *
 * Null when the job doesn't say (no weight entered yet, say): a quote on a
 * guessed zero would be a wrong price, not a missing one.
 */
export function measure(job: TransportJob, uom: RatingUomValue): number | null {
  const loadLegs = handlingTasks(liveTasks(job.tasks)).filter(isLoad);
  type Item = NonNullable<OrderTask['cargoItems']>[number];

  // Pallet-style cargo: each collection picks up its own goods, so the legs
  // add up. Unit cargo: the same tank is on the pick-up AND the load leg, so
  // adding them would count it twice — each unit (by `itemIndex`) is read
  // once, from its latest collection-side leg that recorded the figure. That
  // is the loading, where a weight is actually taken, when both have one.
  const sum = (pick: (i: Item) => number | null | undefined) => {
    let total = 0;
    let any = false;
    if (job.isUnitCargo) {
      const perUnit = new Map<number, number>();
      for (const leg of loadLegs) {
        for (const i of leg.cargoItems ?? []) {
          const v = pick(i);
          if (v != null && v > 0) perUnit.set(i.itemIndex, v); // later legs overwrite
        }
      }
      for (const v of perUnit.values()) {
        total += v;
        any = true;
      }
    } else {
      for (const i of loadLegs.flatMap((t) => t.cargoItems ?? [])) {
        const v = pick(i);
        if (v != null && v > 0) {
          total += v;
          any = true;
        }
      }
    }
    return any ? total : null;
  };

  switch (uom) {
    case 'PerLoad':
      return 1;
    case 'PerContainer':
      return Math.max(1, job.unitCount);
    case 'PerPallet':
    case 'PerHead':
      return sum((i) => i.quantity);
    case 'PerKG':
      return sum((i) => i.weightKg);
    case 'PerTon': {
      const kg = sum((i) => i.weightKg);
      return kg == null ? null : kg / 1000;
    }
    case 'PerCubicMetre':
      return sum((i) => i.volumeM3);
    case 'PerLoadingMetre':
      return sum((i) => i.loadingMetresM);
    case 'PerLitre':
      return sum((i) => i.capacity);
    default:
      return null;
  }
}

const UOM_NOUN: Record<RatingUomValue, string> = {
  PerLoad: 'load',
  PerContainer: 'container',
  PerPallet: 'pallet',
  PerTon: 't',
  PerCubicMetre: 'm³',
  PerLoadingMetre: 'LDM',
  PerLitre: 'L',
  PerKG: 'kg',
  PerHead: 'head',
};

const UOM_MISSING: Record<RatingUomValue, string> = {
  PerLoad: 'loads',
  PerContainer: 'containers',
  PerPallet: 'a pallet count',
  PerTon: 'a weight',
  PerCubicMetre: 'a volume',
  PerLoadingMetre: 'loading metres',
  PerLitre: 'a capacity',
  PerKG: 'a weight',
  PerHead: 'a head count',
};

function qtyLabel(qty: number, uom: RatingUomValue): string {
  const n = Number.isInteger(qty) ? String(qty) : qty.toFixed(2).replace(/\.?0+$/, '');
  const noun = UOM_NOUN[uom];
  const plural = qty === 1 || ['t', 'm³', 'LDM', 'L', 'kg', 'head'].includes(noun) ? noun : `${noun}s`;
  return `${n} ${plural}`;
}

// ── Rate-sheet methods ──────────────────────────────────────────────────────

function sortedBands(bands: CustomerRateBand[]): CustomerRateBand[] {
  return bands.slice().sort((a, b) => a.fromQty - b.fromQty || a.sequence - b.sequence);
}

/**
 * The band a quantity falls in: the first whose upper limit it hasn't passed.
 *
 * <p>
 * Bands are typed as whole-number ranges (1–10, 11–20, 21+), so each one
 * really covers everything above the previous band's top: 11–20 is (10, 20].
 * That's what makes a fractional quantity land sensibly — 10.5 kg is past
 * band 1's top, so band 2 prices it, rather than dropping into the gap between
 * 10 and 11. `lowerBound` below uses the same boundaries, so Band, Range
 * accumulate and Range split all agree on where a band starts.
 * </p>
 */
function bandFor(bands: CustomerRateBand[], qty: number): CustomerRateBand | null {
  for (const b of bands) if (b.toQty == null || qty <= b.toQty) return b;
  // Beyond every closed band with no open-ended one: the top band applies.
  return bands[bands.length - 1] ?? null;
}

/** The rate the Band method uses for a quantity — for explaining a price. */
function bandRateFor(bands: CustomerRateBand[], qty: number): number {
  return bandFor(sortedBands(bands), qty)?.rate ?? 0;
}

/** Where a band's range begins (exclusive), for splitting and reaching bands. */
const lowerBound = (b: CustomerRateBand) => Math.max(0, b.fromQty - 1);

/**
 * The charge a rate-sheet line gives for a quantity, before min/max.
 *
 *   Band        — the total picks one band; its rate × every unit.
 *   Unit        — each unit priced at the band it falls in, summed
 *                 (1st pallet at band 1's rate, 11th at band 2's, …).
 *   RangeAccum  — every band the total reaches adds its flat charge.
 *   RangeSplit  — each portion charged at its own band's rate, like tax
 *                 brackets: 1–10 at A, 11–20 at B, the rest at C.
 */
export function priceBands(method: CustomerRateSheet['rateMethod'], rawBands: CustomerRateBand[], qty: number): number | null {
  const bands = sortedBands(rawBands);
  if (bands.length === 0 || qty <= 0) return null;

  switch (method) {
    case 'Band': {
      const band = bandFor(bands, qty);
      return band ? band.rate * qty : null;
    }
    case 'Unit': {
      // Whole units one by one; a fractional remainder at the next unit's rate.
      const whole = Math.floor(qty);
      let total = 0;
      for (let unit = 1; unit <= whole; unit++) total += bandFor(bands, unit)!.rate;
      const part = qty - whole;
      if (part > 0) total += part * bandFor(bands, whole + 1)!.rate;
      return total;
    }
    case 'RangeAccum':
      // A band is reached once the quantity passes the previous band's top.
      // The first band always is (lower bound 0, qty > 0 checked above).
      return bands.filter((b) => qty > lowerBound(b)).reduce((s, b) => s + b.rate, 0);
    case 'RangeSplit': {
      let total = 0;
      for (const b of bands) {
        const lower = lowerBound(b);
        if (qty <= lower) break;
        const upper = b.toQty ?? Number.POSITIVE_INFINITY;
        total += (Math.min(qty, upper) - lower) * b.rate;
      }
      return total;
    }
    default:
      return null;
  }
}

function clampMinMax(line: CustomerRateSheetLine, charge: number): { net: number; note: string | null } {
  if (line.minCharge != null && charge < line.minCharge) return { net: line.minCharge, note: 'minimum charge' };
  if (line.maxCharge != null && charge > line.maxCharge) return { net: line.maxCharge, note: 'maximum charge' };
  return { net: charge, note: null };
}

const METHOD_WORD: Record<CustomerRateSheet['rateMethod'], string> = {
  Band: 'band',
  Unit: 'per unit',
  RangeAccum: 'range accumulate',
  RangeSplit: 'range split',
};

function money(n: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
}

function priceRateSheet(sheet: CustomerRateSheet, job: TransportJob): TransportQuote {
  const lineOf = (line: CustomerRateSheetLine, qty: number, what: string) => {
    const raw = priceBands(sheet.rateMethod, line.bands, qty);
    if (raw == null) return null;
    const worked = round2(raw);
    const { net, note } = clampMinMax(line, worked);
    // Show the sum and, when min/max changed it, what it was changed to — a
    // cap is otherwise invisible, and a line whose max equals its min prices
    // every quantity the same, which reads as the rate card not working.
    const sum =
      sheet.rateMethod === 'Band'
        ? `${what} × ${money(bandRateFor(line.bands, qty), job.currency)} = ${money(worked, job.currency)}`
        : `${what} (${METHOD_WORD[sheet.rateMethod]}) = ${money(worked, job.currency)}`;
    const adjusted =
      note === 'maximum charge'
        ? `, capped at the ${money(net, job.currency)} maximum`
        : note === 'minimum charge'
          ? `, raised to the ${money(net, job.currency)} minimum`
          : '';
    return { net, text: `${sum}${adjusted}` };
  };

  // Pallet sheets price each pallet type on its own line. The cargo row saves
  // the pallet type by NAME, so resolve it to the variant id the sheet uses.
  const variantLines = sheet.lines.filter((l) => l.subjectKind === 'Variant');
  if (variantLines.length > 0) {
    const byName = new Map(job.palletVariants.map((v) => [v.name, v]));
    const qtyByVariant = new Map<string, { name: string; qty: number }>();
    const items = handlingTasks(liveTasks(job.tasks)).filter(isLoad).flatMap((t) => t.cargoItems ?? []);
    for (const i of items) {
      if (!i.quantity || i.quantity <= 0) continue;
      const variant = i.description ? byName.get(i.description) : undefined;
      if (!variant) {
        return {
          kind: 'unpriced',
          reason: i.description
            ? `The rate sheet has no price for “${i.description}” pallets.`
            : 'A collected pallet line has no pallet type, so the rate sheet can’t price it.',
        };
      }
      const cur = qtyByVariant.get(variant.id) ?? { name: variant.name, qty: 0 };
      cur.qty += i.quantity;
      qtyByVariant.set(variant.id, cur);
    }
    if (qtyByVariant.size === 0)
      return { kind: 'unpriced', reason: 'No pallets have been entered on the collection yet.' };

    let total = 0;
    const parts: string[] = [];
    for (const [variantId, { name, qty }] of qtyByVariant) {
      const line = variantLines.find((l) => l.cargoVariantId === variantId);
      if (!line) return { kind: 'unpriced', reason: `The rate sheet has no price for “${name}” pallets.` };
      const priced = lineOf(line, qty, `${qty} × ${name}`);
      if (!priced) return { kind: 'unpriced', reason: `The rate sheet’s “${name}” line has no bands.` };
      total += priced.net;
      parts.push(`${priced.text} ${money(priced.net, job.currency)}`);
    }
    return {
      kind: 'priced',
      net: round2(total),
      autoApply: sheet.autoApply,
      sheetId: sheet.id,
      source: 'Customer rate sheet',
      detail: parts.join(' + '),
    };
  }

  // Non-pallet sheets: a line per unit of measure. The first one the job
  // gives a quantity for decides — a sheet with a per-kg and a per-pallet line
  // prices by whichever the collection actually recorded.
  const uomLines = sheet.lines.filter((l) => l.subjectKind === 'Uom' && l.uom);
  for (const line of uomLines) {
    const qty = measure(job, line.uom!);
    if (qty == null) continue;
    const priced = lineOf(line, qty, qtyLabel(qty, line.uom!));
    if (!priced) continue;
    return {
      kind: 'priced',
      net: priced.net,
      autoApply: sheet.autoApply,
      sheetId: sheet.id,
      source: 'Customer rate sheet',
      detail: priced.text,
    };
  }
  const wanted = uomLines.map((l) => UOM_MISSING[l.uom!]);
  return {
    kind: 'unpriced',
    reason: wanted.length
      ? `The rate sheet prices by ${[...new Set(wanted)].join(' or ')}, which the collection doesn’t have yet.`
      : 'The rate sheet has no lines.',
  };
}

// ── Route sheets ────────────────────────────────────────────────────────────

/**
 * Which pair of task types marks where a job starts and ends, outermost first.
 *
 * <p>
 * A container or tank job is picked up at A and dropped off at B; its Load and
 * Unload happen along the way, so the lane is the pick-up → drop-off, not the
 * load → unload. A pallet job is Collect → Deliver. Bulk liquids, bulk
 * materials and grain have neither pair and run Load → Unload, so that pair is
 * the last resort rather than being ignored — otherwise those jobs could never
 * match a lane.
 * </p>
 */
const LANE_ENDS: readonly { collect: TaskTypeValue; deliver: TaskTypeValue }[] = [
  { collect: 'PickUp', deliver: 'DropOff' },
  { collect: 'Collect', deliver: 'Deliver' },
  { collect: 'Load', deliver: 'Unload' },
];

/**
 * The legs that define the job's lane: the tasks of the outermost pair the job
 * uses, in order. Every other task — a Load on a container job, a Stop, a
 * Wash — is something done along the way.
 */
function laneLegs(tasks: OrderTask[]): { legs: OrderTask[]; deliver: TaskTypeValue } | null {
  const live = liveTasks(tasks);
  for (const ends of LANE_ENDS) {
    const legs = live.filter((t) => t.type === ends.collect || t.type === ends.deliver);
    if (legs.length > 0) return { legs, deliver: ends.deliver };
  }
  return null;
}

/**
 * Does the job run this lane?
 *
 *   Exact    — one collection at the origin, one delivery at the destination,
 *              no other collection or delivery in between.
 *   Contains — starts with a collection at the origin, ends with a delivery at
 *              the destination, any stops between.
 *
 * "Collection" and "delivery" are the lane's own legs (see `laneLegs`), so a
 * container's Load/Unload, or a Stop, Wash or Inspection, never count against
 * Exact. Lanes are compared by the Location id both ends were picked from; a
 * sheet saved before lanes used Locations has none and can't be matched until
 * its ends are picked again.
 */
function laneMatches(sheet: CustomerRouteSheet, lane: { legs: OrderTask[]; deliver: TaskTypeValue }): boolean {
  const origin = sheet.origin.locationId;
  const destination = sheet.destination.locationId;
  const { legs, deliver } = lane;
  if (!origin || !destination || legs.length < 2) return false;
  const first = legs[0]!;
  const last = legs[legs.length - 1]!;
  if (first.type === deliver || last.type !== deliver) return false;
  if (first.addressId !== origin || last.addressId !== destination) return false;
  return sheet.matchMode === 'Exact' ? legs.length === 2 : true;
}

function priceRouteSheets(sheets: CustomerRouteSheet[], job: TransportJob): TransportQuote {
  const lane = laneLegs(job.tasks);
  const matching = lane ? sheets.filter((s) => laneMatches(s, lane)) : [];
  if (matching.length === 0) {
    const legacy = sheets.some((s) => !s.origin.locationId || !s.destination.locationId);
    if (legacy)
      return {
        kind: 'unpriced',
        reason:
          'A route price for this cargo type was saved before lanes used Locations. Pick its From and To again on the customer’s Rates & Charges to use it.',
      };
    // The customer HAS route prices for this cargo type, so the operator is
    // expecting one — say what the job runs and what's priced, rather than
    // leaving a silent €0.
    return { kind: 'unpriced', reason: laneMismatchReason(sheets, lane, job) };
  }

  // Exact before Contains; among equals, the most recently effective.
  const best = matching.slice().sort(
    (a, b) =>
      (a.matchMode === 'Exact' ? 0 : 1) - (b.matchMode === 'Exact' ? 0 : 1) ||
      b.effectiveFrom.localeCompare(a.effectiveFrom),
  )[0]!;
  const line = best.lines.find((l) => l.cargoDefinitionId === job.cargoDefinitionId)!;

  const qty = measure(job, line.uom);
  if (qty == null)
    return {
      kind: 'unpriced',
      reason: `The route price is by ${UOM_MISSING[line.uom]}, which the collection doesn’t have yet.`,
    };

  // Drops are the lane's own deliveries — drop-offs on a container job, not
  // every Unload along the way.
  const deliveries = lane!.legs.filter((t) => t.type === lane!.deliver).length;
  const extraDrops = Math.max(0, deliveries - 1);
  const base = line.uom === 'PerLoad' ? line.charge : line.charge * qty;
  const drops = extraDrops > 0 && line.extraDrop ? line.extraDrop * extraDrops : 0;

  const from = best.origin.name ?? best.origin.city ?? best.origin.country;
  const to = best.destination.name ?? best.destination.city ?? best.destination.country;
  const parts = [
    line.uom === 'PerLoad'
      ? `${money(line.charge, job.currency)} per load`
      : `${qtyLabel(qty, line.uom)} × ${money(line.charge, job.currency)}`,
  ];
  if (drops > 0)
    parts.push(`${extraDrops} extra drop${extraDrops === 1 ? '' : 's'} × ${money(line.extraDrop!, job.currency)}`);

  return {
    kind: 'priced',
    net: round2(base + drops),
    autoApply: best.autoApply,
    sheetId: best.id,
    source: `Route price ${from} → ${to} (${best.matchMode === 'Exact' ? 'exact lane' : 'with stops'})`,
    detail: parts.join(' + '),
  };
}

const endName = (e: CustomerRouteSheet['origin']) => e.name ?? e.city ?? e.country;
const sameName = (a: string | undefined, b: string | undefined) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Why none of the customer's route prices for this cargo type fit the job.
 *
 * The case worth catching by name: a lane whose ends carry the SAME names as
 * the job's but are different saved Locations — two "Boston Scientific"
 * records, or one renamed since. Lanes match by Location id, so from the
 * screen that looks exactly like a price that should have applied.
 */
function laneMismatchReason(
  sheets: CustomerRouteSheet[],
  lane: ReturnType<typeof laneLegs>,
  job: TransportJob,
): string {
  const priced = sheets.map((s) => `${endName(s.origin)} → ${endName(s.destination)}`);
  const pricedList = [...new Set(priced)].join('; ');

  if (!lane || lane.legs.length < 2 || lane.legs[0]!.type === lane.deliver ||
      lane.legs[lane.legs.length - 1]!.type !== lane.deliver) {
    return `This job needs a collection and a delivery before a route price can apply. Route prices for this cargo type: ${pricedList}.`;
  }

  const first = lane.legs[0]!;
  const last = lane.legs[lane.legs.length - 1]!;
  const fromName = job.locationNames?.[first.addressId];
  const toName = job.locationNames?.[last.addressId];
  const runs = fromName && toName ? `${fromName} → ${toName}` : 'this job’s lane';

  for (const s of sheets) {
    const originIdMatch = s.origin.locationId === first.addressId;
    const destIdMatch = s.destination.locationId === last.addressId;
    if (originIdMatch && destIdMatch && s.matchMode === 'Exact' && lane.legs.length > 2) {
      return `The route price ${endName(s.origin)} → ${endName(s.destination)} is for a direct run only (exact lane), but this job has extra collections or drops.`;
    }
    const originLooksSame = originIdMatch || sameName(endName(s.origin), fromName);
    const destLooksSame = destIdMatch || sameName(endName(s.destination), toName);
    if (originLooksSame && destLooksSame && !(originIdMatch && destIdMatch)) {
      const which = [!originIdMatch && 'From', !destIdMatch && 'To'].filter(Boolean).join(' and ');
      return `The route price ${endName(s.origin)} → ${endName(s.destination)} has the same names but a different saved Location for its ${which}. Re-pick the lane on the customer’s Rates & Charges, or the task’s location, so they are the same Location.`;
    }
  }

  // The closest lane — the one with an end in common, if any — explained end
  // by end. Lanes match by the saved Location, not its name, so the useful
  // thing to say is WHICH Location each side is, and whether the route's one
  // still exists: a Location deleted and re-created under a new name keeps the
  // old name on the route and quietly stops it matching.
  const closest = sheets
    .map((s) => ({
      s,
      score: (s.origin.locationId === first.addressId ? 1 : 0) +
        (s.destination.locationId === last.addressId ? 1 : 0),
    }))
    .sort((a, b) => b.score - a.score)[0]!.s;
  const describe = (routeEnd: CustomerRouteSheet['origin'], jobId: string, jobName: string | undefined, role: string, verb: string) => {
    if (routeEnd.locationId === jobId) return null;
    const known = job.locationNames && routeEnd.locationId ? routeEnd.locationId in job.locationNames : true;
    return `its ${role} is “${endName(routeEnd)}”${known ? '' : ' (a Location that no longer exists)'}, but this job ${verb} “${jobName ?? 'another Location'}”`;
  };
  const differences = [
    describe(closest.origin, first.addressId, fromName, 'From', 'starts at'),
    describe(closest.destination, last.addressId, toName, 'To', 'ends at'),
  ].filter(Boolean);
  if (differences.length > 0) {
    return `The route price ${endName(closest.origin)} → ${endName(closest.destination)} doesn’t apply: ${differences.join('; ')}. Lanes match by the saved Location, so pick the same Location on the customer’s Rates & Charges and on the task.`;
  }
  return `No route price matches ${runs}. Route prices for this cargo type: ${pricedList}.`;
}

// ── Entry point ─────────────────────────────────────────────────────────────

export function quoteTransport(
  rateCard: CustomerRateCard | null | undefined,
  job: TransportJob,
  today: string,
): TransportQuote {
  if (!rateCard) return { kind: 'none' };
  const date = jobDate(job.tasks, today);

  const rateSheets = rateCard.rateSheets.filter(
    (s) => s.cargoDefinitionId === job.cargoDefinitionId && inEffect(s, date),
  );
  const routeSheets = rateCard.routeSheets.filter(
    (s) => inEffect(s, date) && s.lines.some((l) => l.cargoDefinitionId === job.cargoDefinitionId),
  );

  if (rateSheets.length > 0) {
    // The rate-card guard allows one per cargo type per period; if old data
    // holds more, the most recently effective is the current agreement.
    const sheet = rateSheets.slice().sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0]!;
    return currencyMismatch(sheet.currencyIsoCode, job) ?? priceRateSheet(sheet, job);
  }
  if (routeSheets.length > 0) {
    const quote = priceRouteSheets(routeSheets, job);
    if (quote.kind !== 'priced') return quote;
    const sheet = routeSheets.find((s) => s.id === quote.sheetId)!;
    return currencyMismatch(sheet.currencyIsoCode, job) ?? quote;
  }
  return otherCargoOnThisLane(rateCard, job, date) ?? { kind: 'none' };
}

/**
 * The job runs a lane the customer has priced — but only for OTHER cargo types.
 * Said out loud because it's indistinguishable on screen from a price that
 * should have applied, especially when two cargo definitions share a name.
 */
function otherCargoOnThisLane(
  rateCard: CustomerRateCard,
  job: TransportJob,
  date: string,
): TransportQuote | null {
  const lane = laneLegs(job.tasks);
  if (!lane) return null;
  const onLane = rateCard.routeSheets.filter(
    (s) => inEffect(s, date) && laneMatches({ ...s, matchMode: 'Contains' }, lane),
  );
  if (onLane.length === 0) return null;

  const jobCargo = job.cargoNames?.[job.cargoDefinitionId];
  const others = [...new Set(onLane.flatMap((s) => s.lines.map((l) => l.cargoDefinitionId)))];
  const otherNames = others.map((id) => job.cargoNames?.[id] ?? 'another cargo type');
  const sameNamed = jobCargo && otherNames.some((n) => sameName(n, jobCargo));
  return {
    kind: 'unpriced',
    reason: sameNamed
      ? `The route price on this lane is for a different cargo definition that is also called “${jobCargo}”. Pick the order’s cargo, or the route line’s, so they are the same definition.`
      : `The route price on this lane is for ${[...new Set(otherNames)].join(', ')}, not ${jobCargo ?? 'this cargo type'}.`,
  };
}

/**
 * A price in another currency is not this order's price — no conversion is
 * attempted, because a rate agreed in sterling isn't the same agreement at
 * today's exchange rate.
 */
function currencyMismatch(sheetCurrency: string, job: TransportJob): TransportQuote | null {
  return sheetCurrency.toUpperCase() === job.currency.toUpperCase()
    ? null
    : {
        kind: 'unpriced',
        reason: `The customer’s transport price is in ${sheetCurrency}, but this order is in ${job.currency}.`,
      };
}
